import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { alpha } from '@material-ui/core';
import { useSubscription } from '@apollo/client';
import { useStatusDisplay } from '@/hooks/useStatusColor';
import { format } from 'date-fns';
import Carousel from '@/components/carousel/Carousel';
import PanelContainer from '@/components/Panel/PanelContainer';
import { internalMonitorScheduleSubscription } from '@/graph/procedures';
import { useClock } from '@/components/pages/kiosk/HospitalSchedule.hooks';
import { formatMinutes } from '@/components/pages/kiosk/schedule/shared/procedureUtils';
import { optionalFormatWithTimezone } from '@/components/pages/kiosk/tablet/utils';
import { withTheme } from '@/se/theme';
import type { ScheduleColumnView } from '@/contexts/screenConfiguration.types';
import {
  CARD_HEIGHT_REM,
  CARD_HEIGHT_TALL_REM,
  CARD_HEIGHT_TALL_WITH_OR_REM,
  CARD_HEIGHT_WITH_OR_REM,
  DEFAULT_SCHEDULE_COLUMN_VIEW,
  LIST_FOOTER_HEIGHT_REM,
  LIST_FOOTER_HEIGHT_TALL_REM,
} from './scheduleColumn.constants';
import { clampCarouselIntervalSeconds, clampMaxColumns, clampWidthPercent } from './scheduleColumnConfig';
import {
  buildTokens,
  computeBudget,
  computeGeometry,
  computeScheduleColumnLayout,
  countBelowViewport,
  isTallCard,
  listFooterReservePx,
  packPages,
  packTailPage,
  snapViewportToWholeCard,
} from './scheduleColumnPacking';
import type { ScheduledProcedure, ScheduleToken } from './scheduleColumnPacking';
import { presentScheduleToken } from './scheduleColumnDisplay';
import { normalizeScheduleText } from './scheduleColumnText';
import {
  abbreviateScheduledOrName,
  PATIENT_CARD_SCHEDULED_OR_COLUMN_WIDTH,
} from '@/components/patient/PatientSlot/patientCardConfig';

type ScheduleColumnProps = {
  timezone?: string;
  view?: ScheduleColumnView | null;
  widthPercent?: number | null;
  maxColumns?: number | null;
  groupByTime?: boolean | null;
  carouselIntervalSeconds?: number | null;
  showProcedure?: boolean;
  showScheduledOr?: boolean;
  isKiosk?: boolean;
  className?: string;
};

// The subscription is opened by mounting this component; the column is only mounted when
// showScheduleColumn is enabled, so disabled screens open no extra subscription. The date
// variable comes from the hospital-timezone clock — when the day rolls over at midnight,
// the changed variable makes Apollo resubscribe with the new date.
const useScheduledProcedures = (timezone?: string) => {
  const date = useClock(timezone);
  const formattedDate = useMemo(() => format(date, 'YYYY-MM-DD'), [date]);
  const { data, loading } = useSubscription(internalMonitorScheduleSubscription, {
    variables: { date: formattedDate },
    shouldResubscribe: true,
  });
  const procedures: ScheduledProcedure[] = data?.internalMonitorSchedule ?? [];
  const { excludedFromMonitor } = useStatusDisplay('panel');
  return { procedures: excludedFromMonitor.has('PreAdm') ? [] : procedures, loading };
};

// Reports the measured content height (packing budget H) and width (the schedule area width the
// fit loop splits into columns) via one ResizeObserver.
const useContentSize = (): [React.RefObject<HTMLDivElement>, number, number] => {
  const contentRef = useRef<HTMLDivElement>(null);
  // Height and width update together in one state object so a resize commits a single render
  // (React 17 does not batch state updates fired outside event handlers). The functional update
  // below bails out (returns the previous object reference unchanged) only for a genuine no-op.
  // Width commits now trigger a re-render (the fit loop needs the live area width) — acceptable
  // because the area has a fixed footprint, so resizes are rare (viewport changes only), not a
  // per-pixel drag.
  const [size, setSize] = useState({ height: 0, width: 0 });

  useEffect(() => {
    const node = contentRef.current;
    if (!node) return;

    const observer = new ResizeObserver(entries => {
      const rect = entries[0].contentRect;
      setSize(prev =>
        prev.height === rect.height && prev.width === rect.width ? prev : { height: rect.height, width: rect.width }
      );
    });
    observer.observe(node);
    const rect = node.getBoundingClientRect();
    setSize(prev =>
      prev.height === rect.height && prev.width === rect.width ? prev : { height: rect.height, width: rect.width }
    );

    return () => observer.disconnect();
  }, []);

  return [contentRef, size.height, size.width];
};

const ScheduleColumn = ({
  timezone,
  view,
  widthPercent,
  maxColumns,
  groupByTime,
  carouselIntervalSeconds,
  showProcedure,
  showScheduledOr,
  isKiosk,
  className,
}: ScheduleColumnProps) => {
  const { procedures, loading } = useScheduledProcedures(timezone);
  const [contentRef, contentHeight, contentWidth] = useContentSize();
  const [scrollTop, setScrollTop] = useState(0);

  // rAF-throttled so scrolling commits at most one state update per frame; the memoized
  // list body means that update re-renders only the footer count, not the cards.
  const latestScrollTop = useRef(0);
  const scrollFrame = useRef<number | null>(null);
  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    latestScrollTop.current = event.currentTarget.scrollTop;
    if (scrollFrame.current === null) {
      scrollFrame.current = requestAnimationFrame(() => {
        scrollFrame.current = null;
        setScrollTop(latestScrollTop.current);
      });
    }
  };
  useEffect(
    () => () => {
      if (scrollFrame.current !== null) cancelAnimationFrame(scrollFrame.current);
    },
    []
  );

  const configuredMax = clampMaxColumns(maxColumns);
  const effectiveView = view ?? DEFAULT_SCHEDULE_COLUMN_VIEW;
  // Missing ⇒ ungrouped (the default): no time-slot headers, each card carries its own time.
  const grouped = groupByTime ?? false;
  const listMode = effectiveView === 'LIST';
  const carouselIntervalMs = clampCarouselIntervalSeconds(carouselIntervalSeconds) * 1000;

  const tokens = useMemo(() => buildTokens(procedures, grouped), [procedures, grouped]);
  // The fit search: choose the column count k and split the stream into static prefix pages plus
  // the last column's tokens. At configuredMax = 1 this collapses to one column holding the whole
  // stream — the single-column path, bit-exact. `narrow` and card heights come from the chosen
  // per-column width, not the measured area width.
  const layout = useMemo(
    () =>
      computeScheduleColumnLayout(
        tokens,
        contentHeight,
        contentWidth,
        configuredMax,
        effectiveView,
        isKiosk ?? false,
        Boolean(showScheduledOr)
      ),
    [tokens, contentHeight, contentWidth, configuredMax, effectiveView, isKiosk, showScheduledOr]
  );
  const narrow = layout.narrow;

  const renderToken = useCallback(
    (token: ScheduleToken, index: number) => {
      const presentation = presentScheduleToken(token);
      if (presentation.kind === 'header') {
        return <TimeSlotHeader key={`header-${presentation.time}-${index}`}>{presentation.time}</TimeSlotHeader>;
      }

      // Mirrors the Schedule screen: start and finish are vertically aligned in a dedicated left
      // block; physician and compact patient demographics share the prominent row to its right;
      // procedure remains on the secondary row with the scheduled duration in the dim right slot.
      // Arrived / registration cards additively gain a leading green dot and a status chip
      // ("Arrived HH:mm" or, once front desk has started registration, "Registration HH:mm"):
      // on a wide card the chip sits inline before the patient summary; on a narrow (tall) card
      // it drops below the prominent row. The procedure and duration remain in the lower-right grid cell.
      const { procedure, startTime, finishTime, patientSummary, physicianLabel, scheduledOr, durationMinutes } =
        presentation;
      const duration = durationMinutes ? formatMinutes(durationMinutes) : null;
      const registration = procedure.registrationAt != null;
      const arrived = procedure.arrivedAt != null;
      const hasStatus = registration || arrived;
      const statusAt = registration ? procedure.registrationAt : procedure.arrivedAt;
      const statusTime = optionalFormatWithTimezone(statusAt, 'HH:mm', null);
      const statusLabel = registration ? 'Registration' : 'Arrived';
      const tall = isTallCard(procedure, narrow);
      return (
        <Card
          key={procedure.id}
          $arrived={hasStatus}
          $tall={tall}
          $narrow={narrow}
          $showScheduledOr={Boolean(showScheduledOr)}
        >
          <CardTimeBlock $narrow={narrow}>
            <CardStartTime>{startTime}</CardStartTime>
            <CardFinishTime>{finishTime}</CardFinishTime>
            {showScheduledOr && (
              <CardScheduledOr title={scheduledOr || undefined}>
                {abbreviateScheduledOrName(scheduledOr)}
              </CardScheduledOr>
            )}
          </CardTimeBlock>
          <CardDetails>
            <CardMain $narrow={narrow}>
              {hasStatus && <ArrivedDot />}
              <CardPhysician>{physicianLabel}</CardPhysician>
              {hasStatus && !tall && (
                <ArrivedBadge>
                  {statusLabel} {statusTime}
                </ArrivedBadge>
              )}
              <CardPatient>{patientSummary}</CardPatient>
            </CardMain>
            {hasStatus && tall && (
              <CardArrivedRow>
                <ArrivedBadge $compact>
                  {statusLabel} {statusTime}
                </ArrivedBadge>
              </CardArrivedRow>
            )}
          </CardDetails>
          <CardSub>
            <CardProcedure>{showProcedure ? procedure.procedureType : null}</CardProcedure>
            <CardDuration>{duration}</CardDuration>
          </CardSub>
        </Card>
      );
    },
    [showProcedure, showScheduledOr, narrow]
  );

  // Footer geometry and the carousel budget are scoped to the last column (the only overflowing
  // one); prefix columns are static full pages.
  const geometry = useMemo(
    () => computeGeometry(layout.lastColumnTokens, narrow, Boolean(showScheduledOr)),
    [layout.lastColumnTokens, narrow, showScheduledOr]
  );
  const budget = computeBudget(contentHeight, Boolean(showScheduledOr));

  // Columns 1..k−1 are static packed pages, rendered identically in both view modes.
  const prefixColumns = useMemo(
    () =>
      layout.prefixPages.map((page, index) => (
        <ColumnCell key={`prefix-${index}`} $widthPx={layout.columnWidthPx} $first={index === 0}>
          {page.map(renderToken)}
        </ColumnCell>
      )),
    [layout.prefixPages, layout.columnWidthPx, renderToken]
  );

  const listBody = useMemo(
    () => (listMode ? layout.lastColumnTokens.map(renderToken) : null),
    [listMode, layout.lastColumnTokens, renderToken]
  );
  const carouselBody = useMemo(() => {
    if (listMode) return null;
    const pages = packPages(layout.lastColumnTokens, budget, narrow, Boolean(showScheduledOr));
    if (pages.length > 1) {
      pages[pages.length - 1] = packTailPage(layout.lastColumnTokens, budget, narrow, Boolean(showScheduledOr));
    }
    return pages.map((page, pageIndex) => <PageColumn key={pageIndex}>{page.map(renderToken)}</PageColumn>);
  }, [listMode, layout.lastColumnTokens, budget, narrow, renderToken, showScheduledOr]);

  let columns: React.ReactNode;
  if (procedures.length === 0) {
    columns = !loading && <Empty>All scheduled patients have been admitted</Empty>;
  } else {
    // On kiosks a footer counts the cards not visible below the last column's scroll viewport,
    // live-updating as it scrolls. It lives inside the last column's cell (bottom-pinned, width =
    // the cell): only that cell's scroll viewport shrinks by the reserved footer height, while
    // static prefix columns keep the full measured height — the exact capacity the fit model
    // already assumes (H − the resolved footer reserve). The reserve is narrow-dependent and
    // resolved from the same `narrow` the fit search used (layout.narrow), so it matches the reserve
    // the packer already subtracted. The visibility test is unchanged (overflow of the full measured
    // H). The scroll viewport is then snapped DOWN to end on a whole card's bottom (never mid-card at
    // rest); the leftover between the snapped height and H − reserve collapses into the gap above the
    // footer. `countBelowViewport` runs against the snapped height so `+N more` matches the whole
    // cards actually visible.
    const footerShown = listMode && isKiosk && contentHeight > 0 && geometry.totalHeight > contentHeight;
    const snappedViewport = footerShown
      ? snapViewportToWholeCard(
          layout.lastColumnTokens,
          narrow,
          contentHeight - listFooterReservePx(narrow),
          Boolean(showScheduledOr)
        )
      : undefined;

    let footer: React.ReactNode = null;
    if (footerShown && snappedViewport != null) {
      const belowViewport = countBelowViewport(geometry.cardTops, scrollTop, snappedViewport);
      const lastCaseTime = normalizeScheduleText(procedures[procedures.length - 1].startTimeText);
      const moreText = belowViewport > 0 ? `+${belowViewport} more` : '';
      const lastCaseText = lastCaseTime ? `last case ${lastCaseTime}` : '';
      footer = (
        // Pinned height is a pure function of `narrow` (never of the text), so the viewport above
        // cannot flap as the count collapses. Narrow stacks the text as two deliberate lines so it
        // can never wrap-overflow the pinned block.
        <FooterGroup $narrow={narrow}>
          {/* visibility (not conditional render) so the reserved footer area never shifts */}
          <FooterEllipsis $visible={belowViewport > 0}>⋯</FooterEllipsis>
          {narrow ? (
            <FooterCard>
              {moreText ? <FooterLine>{moreText}</FooterLine> : null}
              {lastCaseText ? <FooterLine>{lastCaseText}</FooterLine> : null}
            </FooterCard>
          ) : (
            <FooterCard>
              {moreText}
              {moreText && lastCaseText ? ' · ' : ''}
              {lastCaseText}
            </FooterCard>
          )}
        </FooterGroup>
      );
    }

    // Only the last column overflows — LIST scrolls it, CAROUSEL rotates its remaining pages.
    // When content fits in ≤ k columns, both modes render this column identically (no scroll, no
    // rotation). Kiosk LIST nests the scroll in a ScrollArea so the in-cell footer stays pinned
    // below it (a stable scroller across the footer appearing/disappearing); browser LIST keeps
    // scrolling on the cell itself (no footer), and CAROUSEL clips (the carousel paginates).
    const kioskList = listMode && isKiosk;
    const lastColumn = (
      <ColumnCell
        key="last"
        $widthPx={layout.columnWidthPx}
        $first={layout.prefixPages.length === 0}
        $scrollable={listMode && !isKiosk}
        $footerHost={kioskList}
        onScroll={listMode && !isKiosk ? handleScroll : undefined}
      >
        {listMode ? (
          kioskList ? (
            <ListScrollArea onScroll={handleScroll} $heightPx={snappedViewport}>
              {listBody}
            </ListScrollArea>
          ) : (
            listBody
          )
        ) : (
          <Carousel size={1} interval={carouselIntervalMs}>
            {carouselBody}
          </Carousel>
        )}
        {footer}
      </ColumnCell>
    );
    columns = (
      <>
        {prefixColumns}
        {lastColumn}
      </>
    );
  }

  return (
    <Column className={className} $widthPercent={clampWidthPercent(widthPercent)}>
      <PanelContainer title="Scheduled" patients={procedures.length}>
        {/* Fixed-footprint area the columns split, left-anchored; leftover stays empty. */}
        <Content ref={contentRef}>{columns}</Content>
      </PanelContainer>
    </Column>
  );
};

const Column = styled.div<{ $widthPercent: number }>`
  display: flex;
  flex-flow: column;
  flex: 0 0 ${props => props.$widthPercent}vw;
  min-width: 0;
  padding: 0.5rem 0 1rem 1rem;
`;

// The fixed-footprint schedule area: a left-anchored row of column cells. Both axes are clipped
// here; the last cell owns the vertical scroll (kiosk LIST nests it in a ScrollArea so its
// bottom-pinned footer stays put). Columns are sized in px by the fit loop, so any leftover on
// the right stays empty (the day-draining signal) with the area boundary kept. Its measured
// height is the packing budget H and is unperturbed by the footer, which lives inside a cell.
const Content = styled.div`
  display: flex;
  flex-flow: row;
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
`;

// One schedule column. Width is the fit loop's per-column px (with a full-fill fallback before
// the area is measured, so the single-column case never flashes a zero-width cell); the inter-
// column gutter is border-box padding so k cells sum to exactly the area width. Prefix cells are
// static and clipped. The last cell scrolls in LIST mode: browser LIST scrolls the cell directly
// ($scrollable); kiosk LIST is a $footerHost — it clips and delegates scrolling to an inner
// ScrollArea so the bottom-pinned footer stays fixed, so its gap collapses to 0 (its children
// are the scroll area and the footer, not cards — the 0.5rem inter-card gap lives in ScrollArea).
const ColumnCell = styled.div<{ $widthPx: number; $first?: boolean; $scrollable?: boolean; $footerHost?: boolean }>`
  flex: ${props => (props.$widthPx > 0 ? `0 0 ${props.$widthPx}px` : '1 1 0')};
  box-sizing: border-box;
  display: flex;
  flex-flow: column;
  min-height: 0;
  min-width: 0;
  gap: ${props => (props.$footerHost ? '0' : '0.5rem')};
  padding-left: ${props => (props.$first ? '0' : '1rem')};
  overflow-x: hidden;
  overflow-y: ${props => (props.$scrollable ? 'auto' : 'hidden')};
`;

// The kiosk-LIST scroll viewport nested inside the last cell so the footer can pin below it
// without scrolling. Carries the inter-card gap the cell would otherwise provide. When the footer
// is hidden it grows to fill the cell (flex: 1, real viewport H). When the footer shows it takes an
// explicit snapped height ($heightPx = the whole-card-snapped viewport) instead of flexing, so the
// bottom edge lands on a whole card; the leftover between it and the footer collapses into the gap
// the footer's margin-top:auto opens above the footer.
const ListScrollArea = styled.div<{ $heightPx?: number }>`
  flex: ${props => (props.$heightPx != null ? '0 0 auto' : '1 1 0')};
  ${props =>
    props.$heightPx != null &&
    css`
      height: ${props.$heightPx}px;
    `}
  min-height: 0;
  min-width: 0;
  display: flex;
  flex-flow: column;
  gap: 0.5rem;
  overflow-x: hidden;
  overflow-y: auto;
`;

const PageColumn = styled.div`
  display: flex;
  flex-flow: column;
  gap: 0.5rem;
`;

const TimeSlotHeader = styled.div`
  flex: 0 0 auto;
  height: 1.5rem;
  font-size: 1.125rem;
  font-weight: 600;
  color: ${withTheme((theme: any) => theme.textColor.string())};
`;

const ArrivedDot = styled.span`
  flex: 0 0 auto;
  align-self: center;
  width: 0.6em;
  height: 0.6em;
  border-radius: 50%;
  background-color: ${withTheme((theme: any) => theme.successColor.string())};
`;

const Card = styled.div<{ $arrived?: boolean; $tall?: boolean; $narrow: boolean; $showScheduledOr: boolean }>`
  flex: 0 0 auto;
  height: ${props =>
    props.$showScheduledOr
      ? `${props.$tall ? CARD_HEIGHT_TALL_WITH_OR_REM : CARD_HEIGHT_WITH_OR_REM}rem`
      : props.$tall
        ? `${CARD_HEIGHT_TALL_REM}rem`
        : `${CARD_HEIGHT_REM}rem`};
  box-sizing: border-box;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  grid-template-rows: minmax(0, 1fr) auto;
  column-gap: ${props => (props.$narrow ? '0.5rem' : '0.75rem')};
  row-gap: ${props => (props.$narrow ? '0.5rem' : '0.75rem')};
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  background-color: ${withTheme((theme: any) => alpha(theme.secondaryTextColor.string(), 0.05))};
  overflow: hidden;
  ${props =>
    props.$arrived &&
    css`
      background-color: ${withTheme((theme: any) => alpha(theme.successColor.string(), 0.08))};
      border: 0.125rem solid ${withTheme((theme: any) => alpha(theme.successColor.string(), 0.5))};
    `}
`;

const CardTimeBlock = styled.div<{ $narrow: boolean }>`
  grid-column: 1;
  grid-row: 1 / -1;
  min-width: 3.25rem;
  display: flex;
  flex-flow: column;
  justify-content: flex-start;
  gap: 0.125rem;
  white-space: nowrap;
  font-size: ${props => (props.$narrow ? '1.25em' : '1.5em')};
  line-height: 1.2;
  color: ${withTheme((theme: any) => theme.secondaryTextColor.string())};
`;

const CardStartTime = styled.div`
  font-weight: 600;
`;

const CardFinishTime = styled.div`
  font-size: 1.125rem;
  line-height: 1.1;
  opacity: 0.5;
`;

const CardScheduledOr = styled.div`
  margin-top: auto;
  width: ${PATIENT_CARD_SCHEDULED_OR_COLUMN_WIDTH};
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  font-size: 1.125rem;
  line-height: 1.1;
  color: ${withTheme((theme: any) => theme.textColor.string())};
`;

const CardDetails = styled.div`
  grid-column: 2;
  grid-row: 1;
  min-width: 0;
  display: flex;
  flex-flow: column;
  justify-content: space-between;
`;

const CardMain = styled.div<{ $narrow: boolean }>`
  display: flex;
  justify-content: space-between;
  gap: ${props => (props.$narrow ? '0.25rem' : '0.5rem')};
  font-size: ${props => (props.$narrow ? '1.25em' : '1.5em')};
  line-height: 1.2;
  color: ${withTheme((theme: any) => theme.textColor.string())};
`;

const CardPhysician = styled.div`
  flex: 1 1 0;
  min-width: 2.75rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardPatient = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardSub = styled.div`
  grid-column: 2;
  grid-row: 2;
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 1.125rem;
  line-height: 1.1;
  color: ${withTheme((theme: any) => theme.secondaryTextColor.string())};
`;

const CardProcedure = styled.div`
  flex: 1 1 0;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardDuration = styled.div`
  flex: 0 0 auto;
  white-space: nowrap;
  opacity: 0.5;
`;

// The "Arrived HH:mm" status chip. Wide cards use 0.85em inline with the prominent row. Narrow
// cards use the compact 0.65em variant on their own row so the full timestamp also fits at the
// minimum supported multi-column width. It stays fixed to its content; the physician shrinks first
// and the patient summary is the final ellipsis fallback.
const ArrivedBadge = styled.div<{ $compact?: boolean }>`
  flex: 0 0 auto;
  align-self: baseline;
  white-space: nowrap;
  font-size: ${props => (props.$compact ? '0.65em' : '0.85em')};
  font-weight: 500;
  color: ${withTheme((theme: any) => theme.successColor.string())};
`;

// Narrow (tall) arrived cards only: the compact Arrived badge's own line, directly under the
// prominent row and above the procedure/duration row in the right column.
const CardArrivedRow = styled.div`
  flex: 0 0 auto;
  font-size: 1.25em;
  line-height: 1.4;
`;

// Pinned at the bottom of the last column's cell (margin-top:auto pushes it down so the leftover
// from the snapped scroll viewport collapses into the gap above it). Its rendered height is fixed to
// the narrow-appropriate rem constant (border-box) so it equals the packer's resolved reserve
// exactly — the amount the fit math reserves and the amount the scroll viewport above it gives up.
// The height is a pure function of `narrow`, never of the footer text, so the viewport above cannot
// flap. overflow:hidden is a backstop against any future content growth spilling past the block.
const FooterGroup = styled.div<{ $narrow: boolean }>`
  flex: 0 0 auto;
  box-sizing: border-box;
  margin-top: auto;
  height: ${props => (props.$narrow ? LIST_FOOTER_HEIGHT_TALL_REM : LIST_FOOTER_HEIGHT_REM)}rem;
  display: flex;
  flex-flow: column;
  gap: 0.25rem;
  padding-top: 0.25rem;
  overflow: hidden;
`;

const FooterEllipsis = styled.div<{ $visible: boolean }>`
  text-align: center;
  line-height: 1;
  font-size: 1.5rem;
  letter-spacing: 0.25em;
  opacity: 0.6;
  color: ${withTheme((theme: any) => theme.secondaryTextColor.string())};
  visibility: ${props => (props.$visible ? 'visible' : 'hidden')};
`;

const FooterCard = styled.div`
  border-radius: 4px;
  padding: 0.75rem;
  text-align: center;
  font-size: 1.125rem;
  font-weight: 500;
  color: ${withTheme((theme: any) => theme.textColor.string())};
  background-color: ${withTheme((theme: any) => alpha(theme.secondaryTextColor.string(), 0.12))};
`;

// One deliberate line of the narrow (two-line) footer. Kept single-line with ellipsis so a very
// narrow column degrades to a clipped line rather than wrapping and overflowing the pinned block.
const FooterLine = styled.div`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Empty = styled.div`
  margin: auto;
  opacity: 0.7;
  text-align: center;
  color: ${withTheme((theme: any) => theme.textColor.string())};
`;

export default React.memo(ScheduleColumn);
